import { useListPlugins } from "#src/components/hooks/adapters/usePlugins";
import { PluginAdapter, SortDirection } from "@validereinc/domain";
import { DropdownInput, Form, useForm } from "@validereinc/common-components";
import React, { useCallback, useMemo } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

/**
 * Form component for configuring plugin options in workflow configuration.
 * Only renders if the plugin feature flag is enabled.
 */
export const WorkflowConfigurationPluginOptions = () => {
  const { setPlugin, getPlugin } = useWorkflowConfigurationFormStore();
  const storePluginValues = getPlugin();

  const form = useForm({
    defaultValues: {
      plugin_name: storePluginValues?.plugin_name,
      plugin_version: storePluginValues?.plugin_version
        ? storePluginValues?.plugin_version?.toString()
        : "latest",
    },
  });

  // Fetch all plugins for the dropdown
  const pluginsQuery = useListPlugins({
    page: 1,
    pageSize: 500,
    sortBy: "display_name",
    sortDirection: SortDirection.ASCENDING,
    filters: {
      type: "workflow_variable",
    },
  });

  // Get versions for the selected plugin
  const versionOptions = useMemo(() => {
    const latestVal = { value: "latest", label: "Latest" };
    if (!storePluginValues?.plugin_name || !pluginsQuery.data?.data)
      return [latestVal];

    // Find max version among entries of the selected plugin
    const maxVersion: number | undefined = pluginsQuery.data.data.find(
      (p) => p.name === storePluginValues?.plugin_name
    )?.version;

    if (!maxVersion) return [latestVal];

    return [
      latestVal,
      ...Array.from({ length: maxVersion }, (_, i) => i + 1)
        .reverse()
        .map((val) => ({ value: val.toString(), label: `${val}` })),
    ];
  }, [storePluginValues?.plugin_name, pluginsQuery.data?.data]);

  const handleFetchPluginNames = useCallback(async (payload) => {
    let { data } = await PluginAdapter.getList({
      ...payload,
      pageSize: 500,
      sortBy: "display_name",
      sortDirection: SortDirection.ASCENDING,
      filters: {
        ...(payload.searchTerm
          ? {
              display_name: payload.searchTerm,
              type: "workflow_variable",
            }
          : {
              type: "workflow_variable",
            }),
      },
    });

    if (Array.isArray(payload.value)) {
      data = data.filter((d) => payload.value.includes(d.name));
    }

    return data;
  }, []);

  const handlePluginNameChange = (value: string | string[] | undefined) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    setPlugin({
      plugin_name: newValue,
      plugin_version:
        newValue !== storePluginValues?.plugin_name
          ? undefined
          : storePluginValues?.plugin_version,
    });
  };

  const handlePluginVersionChange = (value: string | string[] | undefined) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    setPlugin({
      plugin_name: storePluginValues?.plugin_name,
      plugin_version:
        newValue === "latest" || !newValue ? undefined : parseInt(newValue),
    });
  };

  return (
    <Form {...form}>
      <DropdownInput
        name="plugin_name"
        label="Plugin Name"
        placeholder="Select a plugin..."
        isFluid
        isClearable
        labelKey="display_name"
        valueKey="name"
        onFetchData={handleFetchPluginNames}
        onChange={(value) => handlePluginNameChange(value)}
      />
      <DropdownInput
        name="plugin_version"
        label="Plugin Version"
        placeholder="Select a version..."
        isFluid
        isClearable
        options={versionOptions}
        labelKey="label"
        valueKey="value"
        defaultValue={
          storePluginValues?.plugin_version
            ? storePluginValues.plugin_version.toString()
            : "latest"
        }
        onChange={(value) => handlePluginVersionChange(value)}
      />
    </Form>
  );
};
